export type GuideError = {
  number: string
  title: string
  belief: string
  reality: string
  action: string
  image: string
  imageAlt: string
}

export const guideErrors: GuideError[] = [
  {
    number: '01',
    title: 'Elegir una crema solo porque es cara.',
    belief: '"Si cuesta más, tiene que ser mejor."',
    reality:
      'El precio nunca garantiza que un producto sea el adecuado para tu piel. Una fórmula costosa puede estar diseñada para necesidades que tu piel no tiene.',
    action:
      'Aprendé primero qué necesita tu piel antes de elegir cualquier producto. La decisión correcta empieza por el diagnóstico, no por el envase.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/6adc3dcd-0e6a-4c9e-a09f-e0aed1c209e1.jpg',
    imageAlt:
      'Frasco de vidrio esmerilado sin marca sobre un pedestal de piedra color crema con luz natural suave',
  },
  {
    number: '02',
    title: 'Comprar porque otra persona obtuvo buenos resultados.',
    belief: '"Si a ella le funcionó, a mí también me va a funcionar."',
    reality:
      'Cada piel es única. Genética, clima, edad, hormonas y hábitos hacen que la misma fórmula se comporte de forma distinta en dos personas.',
    action:
      'Usá las recomendaciones como punto de partida, nunca como conclusión. Observá cómo responde tu propia piel durante algunas semanas.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5270f5fa-133b-4954-9d5b-69232563dfb7.jpg',
    imageAlt: 'Detalle editorial de dos texturas de piel natural bajo luz difusa',
  },
  {
    number: '03',
    title: 'Cambiar de productos demasiado rápido.',
    belief: '"Si en una semana no veo cambios, no está funcionando."',
    reality:
      'La piel se renueva en ciclos. La mayoría de los activos necesitan entre cuatro y doce semanas de uso constante para mostrar resultados reales.',
    action:
      'Elegí pocos productos y mantenelos en el tiempo. La constancia es el ingrediente que ninguna fórmula puede reemplazar.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/5b9994b4-0267-46fa-979a-6fc14c158f9b.jpg',
    imageAlt: 'Tres pomos de cosmética sin marca alineados sobre lino color crema',
  },
  {
    number: '04',
    title: 'No usar protector solar todos los días.',
    belief: '"Si no hay sol o no salgo, no lo necesito."',
    reality:
      'La radiación atraviesa nubes y ventanas durante todo el año. Sin protección, cualquier rutina trabaja contra una corriente invisible.',
    action:
      'Tratá el protector solar como el último paso de tu rutina de mañana, todos los días, en todas las estaciones.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/f0a03580-ab73-4d39-b0d1-1eb27fe2c1e9.jpg',
    imageAlt: 'Luz del sol filtrada por una cortina de lino sobre una pared de yeso color crema',
  },
  {
    number: '05',
    title: 'Usar demasiados activos al mismo tiempo.',
    belief: '"Cuantos más ingredientes potentes, mejor el resultado."',
    reality:
      'Más productos no significan mejores resultados. Combinar activos sin criterio altera la barrera cutánea y produce irritación que se confunde con "detox".',
    action:
      'Incorporá un activo por vez y dejá que la piel se adapte. Una rutina corta y coherente rinde más que una acumulación de promesas.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b54fa982-8783-477f-87c2-9e65c4e00980.jpg',
    imageAlt: 'Pipetas de vidrio transparente sobre papel beige cálido con una hoja verde',
  },
  {
    number: '06',
    title: 'Aplicar los productos en un orden incorrecto.',
    belief: '"El orden da igual, al final todo se absorbe."',
    reality:
      'La textura define la secuencia. Si una crema densa va antes que un sérum ligero, la piel simplemente no recibe lo que le estabas ofreciendo.',
    action:
      'Aplicá de la textura más ligera a la más densa y respetá siempre el mismo orden. La rutina correcta multiplica lo que ya tenés.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/3d68da7d-b08c-46eb-a663-7d8a7ff2f043.jpg',
    imageAlt: 'Cuatro recipientes de cosmética ordenados en diagonal sobre una superficie blanca cálida',
  },
  {
    number: '07',
    title: 'Comprar productos sin conocer realmente tu tipo de piel.',
    belief: '"Mi piel es grasa porque brilla al mediodía."',
    reality:
      'Muchas pieles clasificadas como grasas están en realidad deshidratadas, y muchas rutinas "para piel seca" tratan un problema que no existe.',
    action:
      'Entender tu piel es siempre el primer paso. Con un diagnóstico honesto, cada elección posterior se vuelve más simple y más económica.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/4f012d58-68f1-4fea-b0ca-07737cb41962.jpg',
    imageAlt: 'Retrato editorial de un rostro de perfil con piel natural y luz suave',
  },
]

export type RelatedGuide = {
  index: string
  title: string
  status: 'Completado' | 'Próximamente'
  excerpt: string
}

export const relatedGuides: RelatedGuide[] = [
  {
    index: 'Fresh Guide #1',
    title: 'Los 7 errores que hacen que una crema no funcione',
    status: 'Completado',
    excerpt: 'Por qué el precio y la promesa rara vez explican el resultado.',
  },
  {
    index: 'Fresh Guide #2',
    title: '¿Tu piel está seca o deshidratada?',
    status: 'Próximamente',
    excerpt: 'Dos estados distintos que casi siempre se confunden.',
  },
  {
    index: 'Fresh Guide #3',
    title: 'Cómo elegir cosmética realmente fresca',
    status: 'Próximamente',
    excerpt: 'Qué significa frescura cuando hablamos de fórmulas vivas.',
  },
  {
    index: 'Fresh Guide #4',
    title: 'El orden correcto de una rutina de skincare',
    status: 'Próximamente',
    excerpt: 'Una secuencia simple, de la textura más ligera a la más densa.',
  },
]
